/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { ofType } from 'redux-observable';
import { switchMap, map, mergeMap } from 'rxjs/operators';
import { Observable, type ObservableInput, of } from 'rxjs';

import { dispatch } from '../../store/state/state-manager';
import { ActionManagerInstance } from '../../actions/manager';
import { SUBMIT_SUBSCRIPT } from '../../store/state/actions';
import { getParentContextName } from '../../utils/context-utils';
import * as StateManager from '../../store/state/state-manager';
import * as actions from '../../store/state/actions/action-creators';
import {
  getChildContainers,
  getActiveContainerItemName,
  getContainerItemData,
  getTargetFromContainerItemID
} from '../../container/container-utils';
import type { ActionPayload, SubmitEmbedDataAction, NullReturnedResponseType } from '../open-embedded-data-modal/types';

import { handleFormInvalid, handleFormContextDirty, createOpenEmbeddedDataModal } from './helper';

export const submitSubscript = (action$: Observable<SubmitEmbedDataAction>) => {
  let actionMgrID: string | number = '';
  let actionsOut: SubmitEmbedDataAction[] = [];
  let actionInPayload: ActionPayload;
  let actionResponse = {};

  return action$.pipe(
    ofType(SUBMIT_SUBSCRIPT),
    switchMap((action: SubmitEmbedDataAction) => {
      actionInPayload = action.payload;
      const { isFormValid } = action.payload;
      ({ actionMgrID } = action.payload);

      if (!isFormValid) {
        handleFormInvalid(actionMgrID, action.payload.context);
        return of([]);
      }

      return of(action.payload).pipe(
        mergeMap<ActionPayload, ObservableInput<NullReturnedResponseType>>((payload: ActionPayload) => {
          actionInPayload = payload;
          return of({});
        }),
        mergeMap(async () => {
          const { containerItemID } = actionInPayload;
          const data = StateManager.getDataObject(containerItemID);
          const context = containerItemID;
          const subscriptData = data?.dataInfo.content;
          delete subscriptData.classID;
          const parentContainerItemID = getParentContextName(context);
          const length = getChildContainers(parentContainerItemID).length;
          const activeWorkArea = getChildContainers(parentContainerItemID)[length - 1]
            ? getActiveContainerItemName(getChildContainers(parentContainerItemID)[length - 1])
            : parentContainerItemID;
          const target = getTargetFromContainerItemID(containerItemID);
          const itemData = getContainerItemData(target, containerItemID) || {};
          const pageGroupName = itemData?.referenceList?.replaceAll('.', '');
          const subscriptFieldName = itemData?.subscriptName;
          const workareaContainerData = StateManager.getDataObject(activeWorkArea);
          const updatedWorkareaContainerData = {
            ...workareaContainerData,
            caseInfo: { content: { ...subscriptData } }
          };
          const updateContainerItemPayload = {
            containerItemID: activeWorkArea,
            data: updatedWorkareaContainerData
          };
          actionsOut.push(actions.postProcess(actions.updateContainerItem, updateContainerItemPayload));
          handleFormContextDirty(actionsOut, context, containerItemID);
          const openEmbeddedDataModal = createOpenEmbeddedDataModal({
            itemData,
            workareaContainerData,
            subscriptData,
            pageGroupName,
            activeWorkArea,
            subscriptFieldName,
            actionMgrID,
            containerItemID
          });
          actionsOut.push(openEmbeddedDataModal);

          actionResponse = subscriptData;
          return actionsOut;
        }),
        map(() => {
          ActionManagerInstance.resolveActionByID(actionMgrID, actionResponse);
          dispatch(actionsOut);
          actionsOut = [];
          return actionsOut;
        })
      );
    })
  );
};

export default submitSubscript;
